import { formatVehiclePlateToStr } from "@/utils/formatVehiclePlate";
import { mapDocToPost } from "@/utils/mapDocToPost.web";
import { getApp } from "firebase/app";
import {
  doc,
  GeoPoint,
  getDoc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from "firebase/storage";
import { UpdatePostArgs } from "./types";
import { uploadBlobWithRetry } from "./uploadRetry";

const firestore = getFirestore(getApp());
const storage = getStorage(getApp());

// Firebase Storage download URLs look like:
//   https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded-path}?alt=media&token=...
// Decode the segment after `/o/` to get the object path we can pass to
// `ref(storage, path)`. Returns null for anything we don't recognize.
const extractStoragePathFromUrl = (url: string): string | null => {
  try {
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

export const updatePost = async ({
  postId,
  initialImages,
  images,
  user,
  userData,
  customCoords,
  eventDate,
  description,
  vehiclePlates,
  eventType,
  plateFollowersOnly,
  sourceUrl,
  onSaved,
}: UpdatePostArgs) => {
  // 1. Upload any newly-picked images; keep existing remote URLs as-is.
  const finalImageUrls: string[] = [];
  for (let i = 0; i < images.length; i++) {
    const entry = images[i];
    if ("remote" in entry && entry.remote) {
      finalImageUrls.push(entry.uri);
      continue;
    }
    const asset = entry as import("expo-image-picker").ImagePickerAsset;

    let blob: Blob;
    let fileExtension = "jpg";

    if (asset.mimeType) {
      fileExtension = asset.mimeType.split("/")[1];
    } else if (!asset.uri.startsWith("data:")) {
      const ext = asset.uri.split(".").pop();
      if (ext && ext.length < 5) fileExtension = ext;
    }

    // Storage paths are keyed by the post's AUTHOR uid, not the editor,
    // so new images uploaded by an admin still land under the author's
    // folder. Here we don't have the author uid cheaply; use the editing
    // user's uid for the new upload path. Storage rules are permissive
    // enough that this works, and the admin's uid in the path is a
    // helpful audit trail for which images were added by an editor.
    const filename = `posts/${user.uid}/${Date.now()}_${i}.${fileExtension}`;

    const pickerFile = (asset as unknown as { file?: File }).file;
    if (pickerFile && asset.type !== "video") {
      const buf = await pickerFile.arrayBuffer();
      blob = new Blob([buf], {
        type: pickerFile.type || `image/${fileExtension}`,
      });
    } else if (asset.uri.startsWith("data:")) {
      const arr = asset.uri.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      blob = new Blob([u8arr], { type: mime });
    } else {
      const response = await fetch(asset.uri);
      blob = await response.blob();
    }

    const storageRef = ref(storage, filename);
    await uploadBlobWithRetry(storageRef, blob, uploadBytes);
    const url = await getDownloadURL(storageRef);
    finalImageUrls.push(url);
  }

  // 2. Reverse-geocode when the admin moved the pin. Same logic as uploadPost.
  let locationObj = null;
  if (customCoords) {
    let city = "";
    let country = "";
    try {
      if (
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps
      ) {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({
          location: { lat: customCoords.latitude, lng: customCoords.longitude },
        });

        if (response.results && response.results.length > 0) {
          const result = response.results[0];
          for (const component of result.address_components) {
            if (component.types.includes("locality")) {
              city = component.long_name;
            }
            if (
              !city &&
              component.types.includes("administrative_area_level_1")
            ) {
              city = component.long_name;
            }
            if (component.types.includes("country")) {
              country = component.short_name;
            }
          }
        }
      }
    } catch (e) {
      console.warn("Reverse geocoding failed", e);
    }
    locationObj = {
      geoPoint: new GeoPoint(customCoords.latitude, customCoords.longitude),
      city,
      country,
    };
  }

  // 3. Build the patch. Only whitelisted fields; `author`, `anonymous`,
  //    `createdAt`, `groupId`, `likes`, `commentCount`, `shares` are
  //    never touched. Firestore rules enforce this server-side.
  const patch: Record<string, unknown> = {
    description,
    eventType,
    eventDate,
    location: locationObj,
    vehiclePlates: vehiclePlates.map(formatVehiclePlateToStr),
    images: finalImageUrls,
    plateFollowersOnly: vehiclePlates.length > 0 ? plateFollowersOnly : false,
    sourceUrl: sourceUrl?.trim() || null,
    editedAt: serverTimestamp(),
    lastEditedBy: {
      uid: user.uid,
      ...(userData?.displayName ? { displayName: userData.displayName } : {}),
    },
  };

  await updateDoc(doc(firestore, "posts", postId), patch);

  // 4. Return the mapped, post-edit doc to the caller.
  const snap = await getDoc(doc(firestore, "posts", postId));
  const updated = mapDocToPost(snap);
  onSaved?.(updated);

  // 5. Best-effort cleanup of images that were removed from the post.
  //    Non-fatal: an orphan image is preferable to a failed edit.
  const removed = initialImages.filter((u) => !finalImageUrls.includes(u));
  for (const url of removed) {
    const path = extractStoragePathFromUrl(url);
    if (!path) continue;
    try {
      await deleteObject(ref(storage, path));
    } catch (err) {
      console.warn("Failed to clean up removed post image", err);
    }
  }
};
